/* =============================================================
   USAWorkVisaPage — /visa/usa/work/work-guide
   Detailed self-guide page for USA work visa / green card
   Shows: best bundle card + expandable cards per visa category
   ============================================================= */
import { useState } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ChevronDown, CheckCircle2,
  Briefcase, Star, Users, Zap, Tag,
  ShoppingCart, Phone,
} from "lucide-react";
import Header from "@/components/Header";
import { useConsultation } from "@/contexts/ConsultationModalContext";

const BUNDLE_QPAY = "https://paylink.mn/api/s/inv_ms0ubhod5f9c1f";
const ITEM_QPAY   = "https://paylink.mn/api/s/inv_ms0ubhod5f9c1f";
const ITEM_PRICE  = "₮89,000";
const BUNDLE_PRICE = "₮129,000";

/* ── Visa category data ───────────────────────────────────────── */
interface VisaCategory {
  id: string;
  code: string;
  name: string;
  tagline: string;
  color: string;
  badge?: string;
  description: string;
  eligibility: string[];
  process: string[];
}

const VISA_CATEGORIES: VisaCategory[] = [
  {
    id: "eb3",
    code: "EB-3 Unskilled",
    name: "EB-3 Unskilled — Ажлын шугамаар ногоон карт",
    tagline: "Өндөр ур чадвар шаардахгүй, гэр бүлээрээ явах боломж",
    color: "#34d399",
    badge: "Хамгийн алдартай",
    description:
      "EB-3 Unskilled нь АНУ-д өндөр ур чадвар шаардахгүй ажлын байранд ажиллах замаар ногоон карт авах хамгийн өргөн хэрэглэгддэг зам. Монгол иргэдэд Kennedy Access Center, Seabreeze зэрэг агентлагуудаар дамжин явах боломжтой.",
    eligibility: [
      "Ур чадварын тусгай шаардлага байхгүй",
      "АНУ-ын ажил олгогчтой байх шаардлагатай",
      "Эрүүл мэндийн шалгалт давах",
      "Гэр бүлийн гишүүдийг дагуулах боломжтой",
    ],
    process: [
      "Агентлагаар дамжин ажлын байр олох (Kennedy Access Center, Seabreeze)",
      "Ажил олгогч PERM Labor Certification авах",
      "I-140 Immigrant Petition гаргах",
      "Visa Bulletin дагуу Priority Date хүлээх",
      "Консулын ярилцлага өгөх",
    ],
  },
  {
    id: "niw",
    code: "EB-2 NIW",
    name: "EB-2 NIW — Ажил олгогчгүйгээр ногоон карт",
    tagline: "Өөрийн мэргэжлийн чадвараараа, ажил олгогчгүйгээр",
    color: "#818cf8",
    badge: "Мэргэжилтнүүдэд",
    description:
      "National Interest Waiver (NIW) нь ажил олгогчийн спонсоргүйгээр өөрийн мэргэжлийн чадвар, судалгаа, бизнесийн туршлагаараа ногоон карт авах зам. Монгол иргэдэд ч боломжтой — шаардлагыг бүрэн хангахгүй байсан ч хуулийн зөвлөхтэй зөвлөлдөх нь зүйтэй.",
    eligibility: [
      "Мэргэжлийн өндөр ур чадвартай байх (магистр эсвэл түүнтэй тэнцэх)",
      "Үндэсний ашиг сонирхолд нийцсэн ажил хийдэг байх",
      "Тухайн чиглэлдээ ахисан түвшний мэдлэгтэй",
      "Судалгаа, бизнес, шинжлэх ухаан, урлаг, боловсрол гэх мэт",
    ],
    process: [
      "Мэргэжлийн үнэлгээ хийх (credential evaluation)",
      "I-140 NIW Petition бэлдэх",
      "Дэмжих баримт бичгүүд цуглуулах",
      "USCIS-д өргөдөл гаргах",
      "Консулын ярилцлага өгөх",
    ],
  },
  {
    id: "eb1",
    code: "EB-1 / EB-2",
    name: "EB-1 / EB-2 — Онцгой чадвар, нэрт профессор",
    tagline: "Онцгой авьяас, нэрт эрдэмтэн, ахлах менежерүүдэд",
    color: "#f59e0b",
    description:
      "EB-1A нь онцгой авьяас чадвартай хүмүүст (шагнал, нийтлэл, олон улсын хүлээн зөвшөөрөл), EB-1B нь нэрт профессор, судлаачдад, EB-1C нь олон улсын компанийн ахлах менежерүүдэд зориулагдсан.",
    eligibility: [
      "EB-1A: Олон улсын шагнал, нийтлэл, медиа хамрагдалт",
      "EB-1B: Их сургуулийн профессор, судлаач — 3+ жилийн туршлага",
      "EB-1C: Олон улсын компанийн менежер/захирал — 1+ жил",
      "EB-2: Мэргэжлийн дээд зэргийн боловсрол эсвэл онцгой чадвар",
    ],
    process: [
      "Мэргэжлийн үнэлгээ хийх",
      "Дэмжих баримт бичгүүд цуглуулах",
      "I-140 Petition гаргах",
      "Premium Processing ашиглах боломж (15 ажлын өдөр)",
    ],
  },
  {
    id: "h1b",
    code: "H-1B",
    name: "H-1B — Мэргэжлийн ажилчдын виз",
    tagline: "Мэргэжлийн ажил эрхлэх, спонсор компани шаардлагатай",
    color: "#60a5fa",
    description:
      "H-1B нь мэргэжлийн ажил (инженер, IT, санхүү, эрүүл мэнд гэх мэт) эрхлэхэд зориулсан виз. Жил бүр April 1-д lottery явагддаг — 85,000 виз олгогддог. Спонсор компани олох нь хамгийн чухал алхам.",
    eligibility: [
      "Бакалавр болон түүнээс дээш боловсрол",
      "Мэргэжлийн ажлын байр (specialty occupation)",
      "АНУ-ын ажил олгогч спонсор болох",
      "Lottery-д хамрагдах (жил бүр April 1)",
    ],
    process: [
      "Спонсор компани олох",
      "LCA (Labor Condition Application) гаргах",
      "H-1B Petition бэлдэх",
      "Lottery-д оролцох (April 1)",
      "Lottery-д сонгогдвол виз авах",
    ],
  },
  {
    id: "h2b",
    code: "H-2B",
    name: "H-2B — Улирлын ажилчдын виз",
    tagline: "Зочид буудал, ресторан, аялал жуулчлалын салбарт",
    color: "#34d399",
    description:
      "H-2B нь улирлын эсвэл түр хугацааны ажлын байранд зориулсан виз. Зочид буудал, ресторан, аялал жуулчлал, газар тариалан зэрэг салбарт өргөн хэрэглэгддэг. Жил бүр 66,000 виз олгогддог.",
    eligibility: [
      "Улирлын эсвэл түр хугацааны ажлын байр",
      "АНУ-ын ажил олгогч спонсор болох",
      "Тухайн улирлын ажлын байранд АНУ-ын ажилчид байхгүй байх",
    ],
    process: [
      "Ажил олгогч Temporary Labor Certification авах",
      "H-2B Petition гаргах",
      "Виз мэдүүлэх",
      "Ярилцлага өгөх",
    ],
  },
  {
    id: "o1",
    code: "O-1",
    name: "O-1 — Онцгой авьяас чадвартай хүмүүст",
    tagline: "Урлаг, шинжлэх ухаан, бизнес, спортын онцгой авьяас",
    color: "#f472b6",
    description:
      "O-1 нь шинжлэх ухаан, урлаг, боловсрол, бизнес, спортын чиглэлд онцгой авьяас чадвараа нотолсон хүмүүст зориулсан виз. Lottery байхгүй, жилийн турш мэдүүлж болно.",
    eligibility: [
      "Олон улсын шагнал, нийтлэл, хүлээн зөвшөөрөл",
      "Тухайн чиглэлийн мэргэжилтнүүдийн дэмжлэг",
      "Мэргэжлийн байгууллагын гишүүнчлэл",
      "Өндөр цалин, нэр хүнд бүхий ажлын байр",
    ],
    process: [
      "Дэмжих баримт бичгүүд цуглуулах",
      "O-1 Petition гаргах",
      "Консулын ярилцлага өгөх",
    ],
  },
  {
    id: "l1",
    code: "L-1",
    name: "L-1 — Компанийн дотоод шилжилт",
    tagline: "Олон улсын компанийн ажилтнуудад — менежер, мэргэжилтэн",
    color: "#a78bfa",
    description:
      "L-1 нь олон улсын компанийн ажилтнуудыг АНУ-ын салбарт шилжүүлэхэд зориулсан виз. L-1A менежер/захирлуудад, L-1B мэргэжлийн тусгай мэдлэгтэй ажилтнуудад зориулагдсан.",
    eligibility: [
      "Олон улсын компанийн ажилтан байх",
      "Сүүлийн 3 жилд 1+ жил тухайн компанид ажилласан байх",
      "L-1A: Менежер/захирлын албан тушаал",
      "L-1B: Тусгай мэргэжлийн мэдлэгтэй",
    ],
    process: [
      "Компани L-1 Petition гаргах",
      "Дотоод шилжилтийн баримт бичгүүд бэлдэх",
      "Консулын ярилцлага өгөх",
    ],
  },
];

/* ── Main page ────────────────────────────────────────────────── */
export default function USAWorkVisaPage() {
  const [, navigate] = useLocation();
  const { openConsultation } = useConsultation();
  const [expandedId, setExpandedId] = useState<string | null>("eb3");

  const totalIndividual = VISA_CATEGORIES.length * 89000;
  const totalFormatted  = `₮${totalIndividual.toLocaleString()}`;

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #130D3A 45%, #0F0B35 100%)" }}
    >
      <Header />

      <div className="container pt-24 pb-16 max-w-2xl mx-auto">
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate("/visa/usa/self")}
          className="flex items-center gap-2 mb-8 text-white/50 hover:text-white/80 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Буцах</span>
        </motion.button>

        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="text-3xl">🇺🇸</span>
            <div>
              <h1
                className="text-white font-black text-2xl leading-tight"
                style={{ fontFamily: "'Sora', sans-serif", letterSpacing: "-0.02em" }}
              >
                АНУ ажлын виз / Ногоон карт
              </h1>
              <p className="text-white/50 text-sm mt-0.5">
                Визийн ангиллаа сонгоод дэлгэрэнгүй мэдээлэл авна уу
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
              style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)" }}>
              <Briefcase className="w-3 h-3" />
              7 визийн ангилал
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
              style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)" }}>
              <Users className="w-3 h-3" />
              Гэр бүлийг дагуулах боломж
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
              style={{ background: "rgba(52,211,153,0.12)", color: "#34d399" }}>
              <Star className="w-3 h-3" />
              EB-3 хамгийн алдартай
            </div>
          </div>
        </motion.div>

        <div className="flex flex-col gap-3">

          {/* ── BEST Bundle card ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.04 }}
            className="rounded-2xl overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(234,88,12,0.07) 100%)",
              border: "1px solid rgba(245,158,11,0.30)",
            }}
          >
            <div className="p-5">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
                  style={{ background: "rgba(245,158,11,0.18)", color: "#fbbf24" }}
                >
                  <Zap className="w-3 h-3" />
                  BEST БАГЦ
                </div>
                <div
                  className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
                  style={{ background: "rgba(239,68,68,0.13)", color: "#f87171" }}
                >
                  <Tag className="w-3 h-3" />
                  Хэмнэлт: {totalFormatted}-с дээш
                </div>
              </div>

              <h3 className="text-white font-bold text-lg mb-1">
                Бүх 7 ангиллын гарын авлага
              </h3>
              <p className="text-white/50 text-sm mb-4 leading-relaxed">
                Тус тусад нь авбал <span className="text-white/70">{totalFormatted}</span> болно. Багцаар авбал:
              </p>

              {/* Strikethrough list */}
              <div className="flex flex-col gap-2 mb-5">
                {VISA_CATEGORIES.map((cat, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cat.color }} />
                      <span className="text-white/50 text-sm">{cat.code}</span>
                    </div>
                    <span className="text-white/30 text-sm line-through">{ITEM_PRICE}</span>
                  </div>
                ))}
              </div>

              {/* Price + CTA */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-white/40 text-xs mb-0.5">Багцын үнэ</div>
                  <div
                    className="font-black text-3xl text-white"
                    style={{ fontFamily: "'Sora', sans-serif" }}
                  >
                    {BUNDLE_PRICE}
                  </div>
                </div>
                <a
                  href={BUNDLE_QPAY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all"
                  style={{
                    background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)",
                    color: "white",
                    boxShadow: "0 4px 20px rgba(245,158,11,0.40)",
                  }}
                >
                  <Zap className="w-4 h-4" />
                  Багцаар авах
                </a>
              </div>
            </div>
          </motion.div>

          {/* ── Divider ── */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.07)" }} />
            <span className="text-white/30 text-xs">эсвэл тусад нь сонгоно уу</span>
            <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.07)" }} />
          </div>

          {/* ── Individual visa category cards ── */}
          {VISA_CATEGORIES.map((cat, i) => {
            const isOpen = expandedId === cat.id;
            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + i * 0.05 }}
                className="rounded-2xl overflow-hidden"
                style={{
                  background: isOpen
                    ? `linear-gradient(135deg, ${cat.color}0D 0%, rgba(255,255,255,0.03) 100%)`
                    : "rgba(255,255,255,0.04)",
                  border: isOpen
                    ? `1px solid ${cat.color}40`
                    : "1px solid rgba(255,255,255,0.08)",
                  transition: "all 0.2s",
                }}
              >
                {/* Header row */}
                <button
                  className="w-full text-left p-5 hover:bg-white/[0.02] transition-colors"
                  onClick={() => setExpandedId(isOpen ? null : cat.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5"
                        style={{ background: cat.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span
                            className="text-xs font-bold px-2 py-0.5 rounded-md"
                            style={{ background: `${cat.color}18`, color: cat.color }}
                          >
                            {cat.code}
                          </span>
                          {cat.badge && (
                            <span
                              className="text-xs px-2 py-0.5 rounded-full font-medium"
                              style={{ background: "rgba(52,211,153,0.15)", color: "#34d399" }}
                            >
                              ⭐ {cat.badge}
                            </span>
                          )}
                        </div>
                        <h3 className="text-white font-bold text-base leading-snug">
                          {cat.name}
                        </h3>
                        <p className="text-white/45 text-xs mt-1">{cat.tagline}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-white/50 text-sm font-semibold">{ITEM_PRICE}</span>
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="w-4 h-4 text-white/40" />
                      </motion.div>
                    </div>
                  </div>
                </button>

                {/* Expanded content */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div
                        className="px-5 pb-5 pt-1 flex flex-col gap-4"
                        style={{ borderTop: `1px solid ${cat.color}20` }}
                      >
                        {/* Description */}
                        <p className="text-white/65 text-sm leading-relaxed">
                          {cat.description}
                        </p>

                        {/* Two columns: eligibility + process */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-white/40 text-xs font-semibold uppercase tracking-wide mb-2">
                              Шаардлага
                            </p>
                            <div className="flex flex-col gap-1.5">
                              {cat.eligibility.map((item, j) => (
                                <div key={j} className="flex items-start gap-2">
                                  <CheckCircle2
                                    className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                                    style={{ color: cat.color, opacity: 0.7 }}
                                  />
                                  <span className="text-white/55 text-xs leading-relaxed">{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-white/40 text-xs font-semibold uppercase tracking-wide mb-2">
                              Дэс дараалал
                            </p>
                            <div className="flex flex-col gap-1.5">
                              {cat.process.map((step, j) => (
                                <div key={j} className="flex items-start gap-2">
                                  <span
                                    className="text-xs font-bold flex-shrink-0 w-4 text-right"
                                    style={{ color: cat.color, opacity: 0.7 }}
                                  >
                                    {j + 1}.
                                  </span>
                                  <span className="text-white/55 text-xs leading-relaxed">{step}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* CTA buttons */}
                        <div className="flex flex-col sm:flex-row gap-2 pt-1">
                          <a
                            href={ITEM_QPAY}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex-1"
                            style={{
                              background: `linear-gradient(135deg, ${cat.color}CC 0%, ${cat.color}99 100%)`,
                              color: "white",
                              boxShadow: `0 4px 16px ${cat.color}30`,
                            }}
                          >
                            <ShoppingCart className="w-4 h-4" />
                            Гарын авлага авах — {ITEM_PRICE}
                          </a>
                          <button
                            onClick={() => openConsultation()}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
                            style={{
                              background: "rgba(255,255,255,0.06)",
                              border: "1px solid rgba(255,255,255,0.10)",
                              color: "rgba(255,255,255,0.7)",
                            }}
                          >
                            <Phone className="w-4 h-4" />
                            Зөвлөгөө авах
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 p-5 rounded-2xl text-center"
          style={{
            background: "linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(79,70,229,0.08) 100%)",
            border: "1px solid rgba(124,58,237,0.20)",
          }}
        >
          <Star className="w-6 h-6 text-violet-400 mx-auto mb-2" />
          <h3 className="text-white font-bold text-base mb-1">
            Аль ангилал тохирохоо мэдэхгүй байна уу?
          </h3>
          <p className="text-white/50 text-sm mb-4">
            Манай мэргэжлийн зөвлөх таны нөхцөл байдалд тулгуурлан хамгийн тохиромжтой замыг тодорхойлж өгнө.
          </p>
          <button
            onClick={() => openConsultation()}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)",
              color: "white",
              boxShadow: "0 4px 16px rgba(124,58,237,0.35)",
            }}
          >
            <Phone className="w-4 h-4" />
            Үнэгүй зөвлөгөө авах
          </button>
        </motion.div>
      </div>
    </div>
  );
}
